import React from 'react';

interface ZoomEffectGraphProps {
  baseScale: number;
  toScale: number;
  animationInDuration: number;
  animationOutDuration: number;
  totalDuration: number;
  width?: number;
  height?: number;
}

const ZoomEffectGraph: React.FC<ZoomEffectGraphProps> = ({
  baseScale,
  toScale,
  animationInDuration,
  animationOutDuration,
  totalDuration,
  width = 240,
  height = 60
}) => {
  const graphColor = '#CBD5E1';
  const generatePath = () => {
    const scaleRange = 1.8 - 1.0;
    const padding = height * 0.15;
    const drawHeight = height - 2 * padding;

    // Animation starts and ends at baseline (1.0 scale = 0% zoom)
    const baselineScale = 1.0;
    const baselineY = height - padding - ((baselineScale - 1.0) / scaleRange) * drawHeight;

    // Zoom start level (reached after ease-in)
    const zoomStartScale = baseScale;
    const zoomStartY = height - padding - ((zoomStartScale - 1.0) / scaleRange) * drawHeight;

    // Zoom end level (reached before ease-out)
    const zoomEndScale = toScale;
    const zoomEndY = height - padding - ((zoomEndScale - 1.0) / scaleRange) * drawHeight;

    // Final end always returns to baseline
    const endY = baselineY;

    const easeInEndX = (animationInDuration / totalDuration) * width;
    const easeOutStartX = ((totalDuration - animationOutDuration) / totalDuration) * width;

    let path = `M 0 ${baselineY}`;

    // Ease in phase - from baseline to zoom start level
    if (animationInDuration > 0) {
      const controlX1 = easeInEndX * 0.3;
      const controlY1 = baselineY;
      const controlX2 = easeInEndX * 0.7;
      const controlY2 = zoomStartY;
      path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${easeInEndX} ${zoomStartY}`;
    } else {
      path += ` L ${easeInEndX} ${zoomStartY}`;
    }

    // Hold/transition phase - from zoom start level to zoom end level
    if (easeOutStartX > easeInEndX) {
      // There's a hold phase - create transition between zoom levels
      if (Math.abs(zoomStartScale - zoomEndScale) > 0.01) {
        // Smooth transition between zoom levels if they're different
        const midX = (easeInEndX + easeOutStartX) / 2;
        const controlX1 = easeInEndX + (midX - easeInEndX) * 0.3;
        const controlY1 = zoomStartY;
        const controlX2 = midX + (easeOutStartX - midX) * 0.7;
        const controlY2 = zoomEndY;
        path += ` C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${easeOutStartX} ${zoomEndY}`;
      } else {
        // Flat line if zoom levels are the same
        path += ` L ${easeOutStartX} ${zoomEndY}`;
      }
    } else {
      // No hold phase - ease-in and ease-out meet at the same point
      // Still need to show the zoom level change if different
      if (Math.abs(zoomStartScale - zoomEndScale) > 0.01) {
        // Create a small peak at zoom start level, then transition to zoom end level
        path += ` L ${easeOutStartX} ${zoomEndY}`;
      }
      // If zoom levels are the same, the path naturally continues to the same level
    }

    // Ease out phase - from zoom end level to baseline
    if (animationOutDuration > 0) {
      const controlX3 = easeOutStartX + (width - easeOutStartX) * 0.3;
      const controlY3 = zoomEndY;
      const controlX4 = easeOutStartX + (width - easeOutStartX) * 0.7;
      const controlY4 = endY;
      path += ` C ${controlX3} ${controlY3}, ${controlX4} ${controlY4}, ${width} ${endY}`;
    } else {
      path += ` L ${width} ${endY}`;
    }

    return path;
  };

  const padding = height * 0.15;

  const baselineY = height - padding;

  return (
    <div className="rounded-lg bg-slate-50 py-4">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
        <defs>
          <linearGradient id="zoomGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={graphColor} stopOpacity="0.3" />
            <stop offset="50%" stopColor={graphColor} stopOpacity="0.1" />
            <stop offset="100%" stopColor={graphColor} stopOpacity="0" />
          </linearGradient>
          <pattern id="dottedLine" patternUnits="userSpaceOnUse" width="8" height="2">
            <circle cx="1" cy="1" r="0.5" fill="#9ca3af" />
          </pattern>
        </defs>

        {/* Dotted baseline */}
        <line x1="0" y1={baselineY} x2={width} y2={baselineY} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3,3" />

        {/* Gradient fill under curve */}
        <path d={`${generatePath()} L ${width} ${baselineY} L 0 ${baselineY} Z`} fill="url(#zoomGradient)" />

        {/* Main curve line */}
        <path
          d={generatePath()}
          fill="none"
          stroke={graphColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

export default ZoomEffectGraph;
